import React, { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import Loader from "../../components/Loader/Loader";
import PrimaryButton from "../../widgets/Buttons/PrimaryButton";
import CustomTable from "../../widgets/Table/CustomTable";
import Pagination from "../../widgets/Pagination";
import SuccessSnackbar from "../../widgets/Snackbar/SuccessSnackbar";
import ErrorSnackbar from "../../widgets/Snackbar/ErrorSnackbar";
import { getOffsetPaginationMeta } from "../../widgets/Pagination/paginationUtils";
import {
  useFetchProcessedFileUploadService,
  useFetchUploadedFileHistory,
  useUploadFileService,
} from "../../hooks/useBulkUploadService";
import {
  BULK_UPLOAD_HISTORY_COLUMNS,
  BULK_UPLOAD_HISTORY_PAGE_SIZE_OPTIONS,
  BULK_UPLOAD_MODULE_ID,
  CONSTANTS,
  DEFAULT_BULK_UPLOAD_PAGE_NUMBER,
  DEFAULT_BULK_UPLOAD_PAGE_SIZE,
  FILE_UPLOAD_STATUS,
} from "../../constants/constants";
import { formatDate } from "../../utils/formatDate";
import tokenUtils from "../../utils/tokenUtils";

import styles from "./UploadBulkFile.module.scss";

const { ZERO, ONE } = CONSTANTS;

const handleFileChange = (event, setSelectedFile) => {
  const file = event.target.files[ZERO];
  setSelectedFile(file);
};

const UploadBulkFile = ({
  scriptId,
  uploadTitle,
  historyTitle,
  emptyStateLabel,
}) => {
  const queryClient = useQueryClient();
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [page, setPage] = useState(DEFAULT_BULK_UPLOAD_PAGE_NUMBER);
  const [pageSize, setPageSize] = useState(DEFAULT_BULK_UPLOAD_PAGE_SIZE);
  const [successSnackbar, setSuccessSnackbar] = useState({
    open: false,
    message: "",
  });
  const [errorSnackbar, setErrorSnackbar] = useState({
    open: false,
    message: "",
  });

  const userId = tokenUtils.getUserId();
  const offset = (page - ONE) * pageSize;

  const { mutate: uploadFile, isPending: isUploadLoading } =
    useUploadFileService();

  const {
    data: uploadedFileHistory,
    isLoading: isUploadedFileHistoryLoading,
    isFetching: isUploadedFileHistoryFetching,
  } = useFetchUploadedFileHistory({
    moduleId: BULK_UPLOAD_MODULE_ID,
    offset,
    limit: pageSize,
  });

  const { mutate: fetchProcessedFile, isPending: isFetchProcessedFileLoading } =
    useFetchProcessedFileUploadService();

  const showSnackbarError = (message) => {
    setErrorSnackbar({ open: true, message });
  };

  const showSnackbarSuccess = (message) => {
    setSuccessSnackbar({ open: true, message });
  };

  const handleOnClickDownloadProcessedFile = (fileId) => {
    if (!fileId) {
      showSnackbarError("No file available for download!");
      return;
    }

    fetchProcessedFile(fileId, {
      onSuccess: (data) => {
        const link = document.createElement("a");
        link.href = data.signedUrl;
        link.click();
        showSnackbarSuccess("Processed File downloaded successfully!");
      },
      onError: (err) => {
        showSnackbarError(err.message || "Error fetching Processed File");
      },
    });
  };

  const historyRows = Array.isArray(uploadedFileHistory?.data)
    ? uploadedFileHistory.data
    : [];

  const filteredHistoryRows = historyRows.filter(
    (historyRow) => historyRow.script?.id === scriptId,
  );

  const tableData = filteredHistoryRows.map((historyRow) => ({
    name: historyRow.name,
    status: historyRow.status.name,
    totalRows: historyRow.result?.totalRows,
    errorRows: historyRow.result?.errorRows,
    uploadedAt: formatDate(historyRow.createdAt),
    downloadProcessedFile:
      historyRow.status.id === FILE_UPLOAD_STATUS.PROCESSED ? (
        <PrimaryButton
          onClick={() => handleOnClickDownloadProcessedFile(historyRow.fileId)}
          disabled={
            isUploadedFileHistoryLoading || isFetchProcessedFileLoading
          }
          isLoading={isFetchProcessedFileLoading}
        >
          Download
        </PrimaryButton>
      ) : (
        <PrimaryButton disabled>Not Available Yet</PrimaryButton>
      ),
  }));

  const historyCount = tableData.length;
  const paginationMeta = getOffsetPaginationMeta({
    itemCount: historyRows.length,
    page,
    pageSize,
  });
  const showPagination =
    !isUploadedFileHistoryLoading &&
    (historyCount > ZERO ||
      paginationMeta.hasPreviousPage ||
      paginationMeta.hasNextPage);
  const showExactHistoryCount =
    page === DEFAULT_BULK_UPLOAD_PAGE_NUMBER &&
    !paginationMeta.hasNextPage &&
    historyCount > ZERO;

  const handleUploadSuccess = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    setPage(DEFAULT_BULK_UPLOAD_PAGE_NUMBER);
    queryClient.invalidateQueries({ queryKey: ["uploadedFileHistory"] });
    showSnackbarSuccess("File Uploaded Successfully!");
  };

  const handleUpload = () => {
    if (!selectedFile) {
      showSnackbarError("Please select a file before uploading");
      return;
    }

    uploadFile(
      { selectedFile, userId, scriptId },
      {
        onSuccess: handleUploadSuccess,
        onError: (err) => {
          showSnackbarError(err?.message || "File upload failed");
        },
      },
    );
  };

  const handlePageChange = (nextPage) => {
    setPage(nextPage);
  };

  const handlePageSizeChange = (nextPageSize) => {
    setPageSize(nextPageSize);
    setPage(DEFAULT_BULK_UPLOAD_PAGE_NUMBER);
  };

  return (
    <>
      <div className={styles.uploadPanel}>
        <h2 className={styles.sectionTitle}>{uploadTitle}</h2>
        <div className={styles.uploadContent}>
          <div className={styles.dashedBox}>
            <p className={styles.uploadHint}>
              {selectedFile?.name || "Upload your file here."}
            </p>
            <input
              type="file"
              onChange={(event) => handleFileChange(event, setSelectedFile)}
              ref={fileInputRef}
              className={styles.fileInput}
            />
          </div>
          <div className={styles.uploadActions}>
            <PrimaryButton
              disabled={!selectedFile || isUploadLoading}
              onClick={handleUpload}
              isLoading={isUploadLoading}
            >
              Upload
            </PrimaryButton>
          </div>
        </div>
      </div>

      <section
        className={styles.resultsPanel}
        aria-labelledby="bulk-upload-history-heading"
      >
        <header className={styles.resultsPanelHeader}>
          <div className={styles.resultsTitleRow}>
            <h2
              id="bulk-upload-history-heading"
              className={styles.resultsHeading}
            >
              {historyTitle}
            </h2>
            {isUploadedFileHistoryLoading ? (
              <span className={styles.resultsHeaderMeta}>Loading…</span>
            ) : showExactHistoryCount ? (
              <span className={styles.resultsCountBadge} aria-live="polite">
                {historyCount === ONE ? "1 file" : `${historyCount} files`}
              </span>
            ) : paginationMeta.hasNextPage || paginationMeta.hasPreviousPage ? (
              <span className={styles.resultsHeaderMeta}>
                Page {paginationMeta.page}
              </span>
            ) : null}
          </div>
        </header>
        <div className={styles.resultsBody}>
          {isUploadedFileHistoryLoading ? (
            <div className={styles.resultsStateWrap}>
              <Loader />
            </div>
          ) : historyCount > ZERO ? (
            <CustomTable
              columns={BULK_UPLOAD_HISTORY_COLUMNS}
              data={tableData}
            />
          ) : paginationMeta.hasPreviousPage || paginationMeta.hasNextPage ? (
            <p className={styles.resultsEmpty}>No files on this page.</p>
          ) : (
            <p className={styles.resultsEmpty}>
              No {emptyStateLabel} files uploaded yet. Choose a file above and
              click <strong>Upload</strong> to see it listed here.
            </p>
          )}
        </div>
        {showPagination && (
          <footer className={styles.resultsFooter}>
            <Pagination
              page={paginationMeta.page}
              totalPages={paginationMeta.totalPages}
              pageSize={paginationMeta.pageSize}
              pageSizeOptions={BULK_UPLOAD_HISTORY_PAGE_SIZE_OPTIONS}
              summaryLabel={`Page ${paginationMeta.page}`}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              disabled={isUploadedFileHistoryFetching}
            />
          </footer>
        )}
      </section>

      <SuccessSnackbar
        open={successSnackbar.open}
        message={successSnackbar.message}
        onClose={() => setSuccessSnackbar({ open: false, message: "" })}
      />
      <ErrorSnackbar
        open={errorSnackbar.open}
        message={errorSnackbar.message}
        onClose={() => setErrorSnackbar({ open: false, message: "" })}
      />
    </>
  );
};

export default UploadBulkFile;
